namespace distill.dashboards {

    export const DASH_EVENTS = {
        ALIAS_CHANGED: 'alias:changed',
        METRIC_REMOVED: 'metric:removed',
        AI_QUERY_SEND: 'aiQuery:send',
        AI_QUERY_RESULT: 'aiQuery:result',
        AI_QUERY_CLEAR: 'aiQuery:clear',
        REFRESH_RATE_CHANGED: 'refresh:rate_changed'
    };
    export interface IChatMessage {
        Role: 'user' | 'model';
        Parts: { Text: string }[];
    }
    export interface IAiQuerySendPayload {
        component: IComponent;
        userQuery: string;
        history?: IChatMessage[];
        historySignature?: string;
    }
    
    // Mirrors Appenate.Distill.Dtos.Internal.InternalDistillErrorType (serialised by member name).
    export enum InternalDistillErrorType {
        UsageLimitExceeded = 'UsageLimitExceeded',
        AuthenticationRequired = 'AuthenticationRequired',
        PlanNotSupported = 'PlanNotSupported'
    }

    export interface IAskQueryResponse {
        Answer: string;
        ErrorType?: InternalDistillErrorType;
        Charts: IAskChart[];
        Turns?: IChatMessage[];
        HistorySignature?: string;
    }

    export interface IAiQueryResultPayload {
        componentId: string;
        success: boolean;
        response: IAskQueryResponse | string | null;
        upgradeUrl?: string;
    }

    export interface IAskChart {
        Type: string;
        Title: string;
        Labels: string[];
        Series: IAskChartSeries[];
        Stacked?: boolean;
    }

    export interface IAskChartSeries {
        Name: string;
        Data: (number | null)[];
    }

    export interface ITableExportArgs {
        url?: string;
        accessToken?: string;
        fileName: string;
        sheetName: string;
        componentId: string;
        filters: IDashFilterSelection[];
        sortOverrides?: IViewSortOverride[];
    }

    export const DASH_PROPERTY_TYPE = {
        LEGEND_STYLE: 'legendStyle',
        DATASET_SELECTOR: 'datasetSelector',
        FILTERS: 'filtersEditor',
        COLUMN: 'columnSelector',
        METRICS: 'metricsEditor',
        PROJECTION: 'projectionSelector',
        AGGREGATION_FILTERS: 'aggregationFiltersEditor',
        BREAKDOWN_BY_LIMIT: 'breakDownByLimitEditor',
        SORT_ORDER: 'sortOrderEditor',
        COLOR_PALETTE: 'colorPalette',
        SCOPE_ITEMS: 'scopeItemsEditor'
    }

    export enum DashboardGlobalFilterType {
        DateRange = 'DateRange',
        Dropdown = 'Dropdown',
        NumberRange = 'NumberRange',
        Text = 'Text'
    }

    export interface IDashboardGlobalFilter {
        id: string;
        label: string;
        dataName?: string | null;
        type: DashboardGlobalFilterType;
        
        // Dropdown-only
        sourceDatasetId?: string | null;
        displayColumn?: string | null;
        valueColumn?: string | null;
    }

    export interface IDashboardDesign extends appn.designerv2.IDesign {
        globalFilters?: IDashboardGlobalFilter[];
    }

    export interface IGlobalFilterBinding {
        globalFilterId: string;
        datasetIndex?: number;
        columnId?: string;
        metricIndex?: number;
        operator?: string;
    }

    export interface IGlobalFilterOption {
        display: string;
        value: string;
        count?: number;
    }

    // Mirrors the server's FilterOptionsQuery.MAX_FILTER_OPTIONS
    export const FILTER_OPTIONS_LIMIT = 200;

    export const GLOBAL_FILTER_COMPATIBLE_COLUMN_TYPES: { [type: string]: string[] } = {
        [DashboardGlobalFilterType.DateRange]: ['Date'],
        [DashboardGlobalFilterType.NumberRange]: ['Number'],
        [DashboardGlobalFilterType.Text]: ['Text', 'LongText', 'Number', 'Date', 'Boolean', 'UniqueIdentifier']
    };

    export enum ComponentType {
        titleBar = 'titleBar',
        group = 'group',
        pieChart = 'pieChart',
        lineChart = 'lineChart',
        barChart = 'barChart',
        heading = 'heading',
        valueLabel = 'valueLabel',
        webView = 'webView',
        embeddedDash = 'embeddedDash',
        table = 'table',
        distillQuery = 'distillQuery',
    }

    export enum InsightTimeGrain {
        None = 'None',
        Second = 'Second',
        Minute = 'Minute',
        Hour = 'Hour',
        Day = 'Day',
        Week = 'Week',
        Month = 'Month',
        Quarter = 'Quarter',
        Year = 'Year'
    }

    export interface ILegendStyle {
        verticalPosition?: 'top' | 'middle' | 'bottom';
        horizontalPosition?: 'left' | 'center' | 'right';
    }

    export interface IDatasetColumnMeta {
        id: string;
        displayName: string;
        designation: string | null;
        type: string;
    }

    export interface IDatasetMeta {
        id: string;
        name: string;
        origin?: string;
        originIconUrl?: string;
        originDisplayName?: string;
        cruxEntityId?: string;
        cruxParentDatasetId?: string;
        cruxParentDatasetName?: string;
        columns: IDatasetColumnMeta[];
    }

    export interface IDatasetReference {
        id: string;
        alias: string;
    }

    export interface IDatasetLink {
        primaryDatasetIndex?: number;
        primaryColumnId?: string;
        linkedDataset: IDatasetReference;
        linkedColumnId?: string;
    }

    export interface IDataQueryFilter {
        groupOperator?: 'AND' | 'OR';
        groupConditions?: IDataQueryFilter[];
        datasetIndex?: number;
        columnId?: string;
        operator?: string;
        value?: string;
        value2?: string;
        dynamicValue?: string;
        dynamicValue2?: string;
        valueDatasetIndex?: number;
        valueColumnId?: string;
        value2DatasetIndex?: number;
        value2ColumnId?: string;
        globalFilterId?: string;
    }

    export interface IDataQueryAggregationFilter {
        groupOperator?: 'AND' | 'OR';
        groupConditions?: IDataQueryAggregationFilter[];
        column?: IDataQueryColumnIdentifier; // Used for GroupBy/BreakDownBy
        metricIndex?: number;             // Used for Metrics
        operator?: string;
        value?: string;
        value2?: string;
        dynamicValue?: string;
        dynamicValue2?: string;
        valueColumn?: IDataQueryColumnIdentifier;  // References GroupBy/BreakDownBy column
        valueMetricIndex?: number;                  // References metric by index
        value2Column?: IDataQueryColumnIdentifier;
        value2MetricIndex?: number;
        globalFilterId?: string;
    }

    export interface IDataQueryColumnIdentifier {
        datasetIndex?: number;
        columnId?: string;
        alias?: string;
        timeGrain?: InsightTimeGrain;
        filterId?: string;
    }

    export interface IDataQueryProjection {
        columns: IDataQueryColumnIdentifier[];
    }

    export interface IDataQueryMetric extends IDataQueryColumnIdentifier {
        function: 'Count' | 'CountDistinct' | 'Sum' | 'Avg' | 'Min' | 'Max';
        alias: string; // not optional on metrics
    }

    export interface IBreakDownByLimit {
        type: 'None' | 'TopOverall' | 'Top' | 'BottomOverall' | 'Bottom';
        limit?: number;
        metricIndex?: number;
    }

    export interface IDataQueryAggregation {
        groupBy?: IDataQueryColumnIdentifier;
        breakDownBy?: IDataQueryColumnIdentifier;
        metrics: IDataQueryMetric[];
        breakDownByLimit?: IBreakDownByLimit;
    }

    export interface IDataQuerySortOrder {
        type: 'Asc' | 'Desc';
        column?: IDataQueryColumnIdentifier;
        metricIndex?: number;
    }

    export interface ITableSortOverride {
        header: string;
        dataQueryName: string;
        orders: IDataQuerySortOrder[];
    }

    export interface IViewSortOverride {
        componentId: string;
        dataQueryName: string;
        sortOrders: IDataQuerySortOrder[];
    }

    export interface IDashFilterSelection {
        filterId: string;
        values?: string[];
        from?: string;
        to?: string;
    }

    export interface IColumnFilterState {
        values?: string[];
        from?: string;
        to?: string;
    }

    export interface IDataQuery {
        name: string;
        primaryDataset: IDatasetReference;
        links: IDatasetLink[];
        filter: IDataQueryFilter | null;
        aggregation: IDataQueryAggregation | null;
        aggregationFilter: IDataQueryAggregationFilter | null;
        projection: IDataQueryProjection | null;
        sortOrders: IDataQuerySortOrder[];
        limit: number | null;
        offset: number | null;
        globalFilterBindings?: IGlobalFilterBinding[];
    }

    export interface IDataQueryResult {
        Name: string;
        Headers: string[];
        ObjectResult: { [key: string]: any }[];
        Error: string | null;
        TotalCount?: number;
    }

    export interface IDataQueryResponse {
        Views: IDataQueryResult[];
        Error: string | null;
    }

    export interface IReferenceLine {
        label?: string;
        type: 'Constant' | 'Average' | 'Min' | 'Max';
        value?: number;
        color?: string;
        lineStyle?: 'solid' | 'dashed' | 'dotted';
    }

    export interface IDashboardComponentLayout {
        chartStyle?: 'Pie' | 'Doughnut' | 'Line' | 'Area';
        showLegend?: boolean;
        legendStyle?: ILegendStyle;
        showDataLabels?: boolean;
        roseType?: 'None' | 'Area' | 'Radius';
        connectNulls?: boolean;
        stackingMode?: 'None' | 'Normal' | 'Percentage';
        pageSize?: number; // For Table
        kpiStyle?: 'Basic' | 'Sparkline';
        summaryMode?: 'Latest' | 'Aggregate';
        showTrend?: boolean;
        trendDisplayMode?: 'Percent' | 'Absolute' | 'Both';
        showProgressBar?: boolean;
        headingAlign?: 'Left' | 'Center' | 'Right';
        displaySortOrders?: IDataQuerySortOrder[];
        chartMarginTop?: number;
        chartMarginBottom?: number;
        chartMarginLeft?: number;
        chartMarginRight?: number;
        enableCustomGeometry?: boolean;
        pieOuterRadius?: number;
        pieInnerRadius?: number;
        pieCenterX?: number;
        pieCenterY?: number;
        lineSmooth?: boolean;
        lineWidth?: number;
        yAxisMin?: number;
        yAxisMax?: number;
        markerStyle?: 'Cycle' | 'Circle' | 'None';
        referenceLines?: IReferenceLine[];
    }

    export interface IDashboardComponentStyling {
        colorPalette?: 'Vibrant' | 'Pastel' | 'Grayscale' | 'Corporate' | 'Warm' | 'Cool' | 'Nature' | 'Neon' | 'Custom';
        customColorPalette?: string[];
        xAxisLabelRotation?: number;
        yAxisScale?: 'Linear' | 'Logarithmic';
        yAxisLeftTitle?: string;
        yAxisRightTitle?: string;
        yAxisGridLines?: 'None' | 'Horizontal';
        yAxisValueFormatting?: IValueFormatConfig;
        xAxisValueFormatting?: IValueFormatConfig;
        tooltipTrigger?: 'Item' | 'Axis' | 'None';
        sendButtonStyle?: string;
        sentMessageTextColor?: string;
        sentMessageBgColor?: string;
        responseMessageTextColor?: string;
        responseMessageBgColor?: string;
        sendButtonTextColor?: string;
        sparklineColor?: string;
        trendColorMode?: 'Standard' | 'Inverted';
        valuePrefix?: string;
        valueSuffix?: string;
        valueDecimals?: number;
        valueColor?: string;
        enableThresholds?: boolean;
        thresholdType?: 'PercentOfTarget' | 'Absolute';
        thresholdLogic?: 'Standard' | 'Inverted';
        warningThreshold?: number;
        dangerThreshold?: number;
        applyConditionTo?: 'Card' | 'Text' | 'Value';
        headingSize?: number;
        headingColor?: string;
        isBold?: boolean;
        isItalic?: boolean;
        headerBgColor?: string;
        outlineColor?: string;
        inputBgColor?: string;
        dataLabelValueFormatting?: IValueFormatConfig;
        legendValueFormatting?: IValueFormatConfig;
        tooltipValueFormatting?: IValueFormatConfig;
        tooltipBgColor?: string;
        tooltipBgOpacity?: number;
    }
    export interface IValueFormatConfig {
        textStyle?: IFontStyle;
        format?: 'Value' | 'Percent' | 'Value (Percent)' | 'Category' | 'Category: Value' | 'Category: Percent' | 'Category: Value (Percent)';
        factor?: number;
        decimals?: number;
        prefix?: string;
        suffix?: string;
    }

    export interface IArtifactScopeItem {
        id: string;
        recursive: boolean;
    }

    export interface IDashboardComponent extends appn.designerv2.IComponent {
        title?: appn.designerv2.ITranslationText;
        hideTitle?: boolean;
        refreshInterval?: '30s' | '1m' | '5m' | '15m' | '1h';
        dataQuery0?: IDataQuery;
        welcomeMessage?: string;
        systemInstruction?: string;
        scopeItems?: IArtifactScopeItem[];
        targetValue?: number;
        headingText?: string;
        layout?: IDashboardComponentLayout;
        styling?: IDashboardComponentStyling;
    }

    export interface ChartDataModel {
        labels: string[];
        series: {
            name: string;
            data: (number | null)[];
            metricConfig: IDataQueryMetric;
            breakdownValue?: string;
        }[];
        hasRealData: boolean;
        errorMessage?: string;
    }

    export interface ChartContext {
        component: IDashboardComponent;
        data: ChartDataModel;
        options: any;
        colors: string[];
    }
}
